import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, HStack, Stack, Text } from "@chakra-ui/react";
import { FiArrowRight, FiUploadCloud } from "react-icons/fi";
import { CVUploadModal } from "src/features/pro-portfolio/cv-prefill/cv-upload-modal";
import { PrefillReview } from "src/features/pro-portfolio/cv-prefill/prefill-review";
import { PrefillStep, ResumeData } from "src/features/pro-portfolio/cv-prefill/types";
import { PrefillImages } from "src/features/pro-portfolio/cv-prefill/prefill-images";
import { logEvent } from "src/utlis/analytics";

const SetupLaborPass: React.FC = () => {
	const navigate = useNavigate();
	const [step, setStep] = useState<PrefillStep>("interstitial");
	const [isUploadOpen, setIsUploadOpen] = useState(false);
	const [extractedData, setExtractedData] = useState<ResumeData | null>(null);

	const handleUploadSuccess = useCallback((data: ResumeData) => {
		setExtractedData(data);
		setIsUploadOpen(false);
		setStep("review");
	}, []);

	const handlePrefillComplete = useCallback(() => {
		setStep("images");
	}, []);

	const handleImagesComplete = useCallback(() => {
		navigate("/my-laborpass");
	}, [navigate]);

	const handleStartOver = useCallback(() => {
		setExtractedData(null);
		setStep("interstitial");
	}, []);

	const handleManualFill = useCallback(() => {
		logEvent("manual_fill_path_selected", { platform: "web" });
		navigate("/my-laborpass");
	}, [navigate]);

	useEffect(() => {
		logEvent("laborpass_setup_viewed", { platform: "web" });
	}, []);

	// Image attachment step
	if (step === "images") {
		return <PrefillImages onComplete={handleImagesComplete} />;
	}

	// Review step
	if (step === "review" && extractedData) {
		return (
			<PrefillReview
				resumeData={extractedData}
				onComplete={handlePrefillComplete}
				onStartOver={handleStartOver}
			/>
		);
	}

	// Interstitial step (default)
	return (
		<>
			<Stack
				spacing={0}
				className="flex flex-col lg:border-[1px] border-[#C9C9C9] bg-white h-auto lg:min-h-[70vh] justify-between"
			>
				<Stack
					spacing={8}
					px={{ base: 4, lg: 8 }}
					py={{ base: 6, lg: 12 }}
					flex={1}
					overflowY="auto"
				>
						<Box>
						<Text
							fontSize={{ base: "2xl", lg: "3xl" }}
							fontWeight="bold"
							color="#121212"
							fontFamily="AeonikPro"
						>
							Set up your LaborPass
						</Text>
						<Text color="gray.600" mt={2} fontSize="lg">
							Your LaborPass is your professional portfolio that
							showcases your skills and experience to clients.
						</Text>
					</Box>

					<Box
						className="grid grid-cols-1 lg:grid-cols-2 gap-8"
						flex={1}
					>
						{/* What a great LaborPass looks like */}
						<Box>
							<Text
								fontWeight="bold"
								fontSize="lg"
								mb={4}
								color="#121212"
							>
								What a great LaborPass looks like
							</Text>
							<Stack spacing={3}>
								{[
									"A professional summary that highlights your expertise",
									"Detailed employment history with descriptions",
									"Photos of your completed work and projects",
									"Relevant certifications and qualifications",
									"Education background and training",
									"A complete profile that builds client trust",
								].map((point, i) => (
									<HStack key={i} alignItems="start">
										<Box
											w={2}
											h={2}
											rounded="full"
											bg="green.400"
											mt={2}
											flexShrink={0}
										/>
										<Text color="gray.700">{point}</Text>
									</HStack>
								))}
							</Stack>
						</Box>

						{/* Benefits */}
						<Box>
							<Text
								fontWeight="bold"
								fontSize="lg"
								mb={4}
								color="#121212"
							>
								Benefits of a complete LaborPass
							</Text>
							<Stack spacing={3}>
								{[
									"Get matched with more job opportunities",
									"Stand out to clients and employers",
									"Build credibility with verified work history",
									"Increase your earning potential",
								].map((point, i) => (
									<HStack key={i} alignItems="start">
										<Box
											w={2}
											h={2}
											rounded="full"
											bg="primary.500"
											mt={2}
											flexShrink={0}
										/>
										<Text color="gray.700">{point}</Text>
									</HStack>
								))}
							</Stack>
						</Box>
					</Box>
				</Stack>

				{/* CTA Footer */}
				<Stack
					py={{ base: 4, lg: 8 }}
					px={{ base: 4, lg: 8 }}
					borderTop={{ base: "none", lg: "1px solid #E7E7E7" }}
					direction={{ base: "column", lg: "row" }}
					spacing={4}
					justifyContent="end"
					alignItems={{ base: "stretch", lg: "center" }}
				>
					<Button
						variant="outline"
						rounded={0}
						px={8}
						py={6}
						onClick={handleManualFill}
						rightIcon={<FiArrowRight size="18px" />}
					>
						Fill in manually
					</Button>
					<Button
						colorScheme="brand"
						rounded={0}
						px={8}
						py={6}
						leftIcon={<FiUploadCloud size="18px" />}
						onClick={() => {
							logEvent("cv_upload_path_selected", { platform: "web" });
							setIsUploadOpen(true);
						}}
					>
						Upload my CV
					</Button>
				</Stack>
			</Stack>

			<CVUploadModal
				isOpen={isUploadOpen}
				onClose={() => setIsUploadOpen(false)}
				onSuccess={handleUploadSuccess}
				onManualFill={handleManualFill}
			/>
		</>
	);
};

export default SetupLaborPass;
